/**
 * PipelineStageRow — single row in the Entity Drilldown's vertical pipeline.
 *
 * One row per stage 1–18. Renders the stage number, label, status icon,
 * recorded date (or outcome chip for inspection stages), and inline lock
 * reason when not editable. For editable date stages (6, 7, 8, 10, 12, 14,
 * 15, 16, 17, 18) a date input is inline. For inspection stages (9, 11, 13)
 * an "Edit" / "Record" button opens the drawer.
 *
 * Visual states encoded via icon + color:
 *   - completed: green check
 *   - current: blue clock (active, editable)
 *   - locked: gray lock + inline reason
 *   - skipped: faded dash (auto-skip case)
 *   - not-applicable: muted "—" (sub-part rows for 6-18)
 */

import { CheckCircle2, Clock, Lock, MinusCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { todayPktIsoDate } from '@procurement/shared';
import { DmyDateInput } from '@/components/forms/dmy-date-input';
import { cn } from '@/lib/cn';
import type { InspectionOutcome } from '@/lib/pipeline-queries';
import type { StageRowState } from '@/lib/pipeline-state';

interface PipelineStageRowProps {
  row: StageRowState;
  /** Called when the user submits an inline date edit (part-stage rows only). */
  onSubmitDate?: (stageNumber: number, completedDate: string) => Promise<void>;
  /** Called when the user clicks the Edit/Record button on an inspection row. */
  onOpenInspection?: (stageNumber: 9 | 11 | 13) => void;
}

const STATUS_ICON = {
  completed: CheckCircle2,
  current: Clock,
  locked: Lock,
  skipped: MinusCircle,
  'not-applicable': MinusCircle,
} as const;

const STATUS_COLOR = {
  completed: 'text-success',
  current: 'text-accent',
  locked: 'text-tertiary',
  skipped: 'text-tertiary',
  'not-applicable': 'text-tertiary/60',
} as const;

const OUTCOME_CHIP_CLASS: Record<InspectionOutcome, string> = {
  ACCEPTED: 'border-success/30 bg-success/10 text-success-strong',
  OBSERVATION: 'border-warning/30 bg-warning/10 text-warning-strong',
  REJECTED: 'border-danger/30 bg-danger/10 text-danger-strong',
};

function OutcomeChip({ outcome }: { outcome: InspectionOutcome }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm border px-1.5 py-0.5 text-xs font-medium',
        OUTCOME_CHIP_CLASS[outcome],
      )}
    >
      {outcome.charAt(0) + outcome.slice(1).toLowerCase()}
    </span>
  );
}

function todayIso(): string {
  // Patch E18 — PKT date default. UTC was rolling tomorrow for late-PKT users.
  return todayPktIsoDate();
}

export function PipelineStageRow({
  row,
  onSubmitDate,
  onOpenInspection,
}: PipelineStageRowProps) {
  const Icon = STATUS_ICON[row.status];
  const [draftDate, setDraftDate] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!onSubmitDate || !draftDate) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmitDate(row.stageNumber, draftDate);
      setDraftDate('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className={cn(
        'flex items-start gap-3 border-b border-border-subtle px-4 py-3 last:border-b-0',
        row.status === 'not-applicable' && 'opacity-40',
      )}
    >
      {/* Stage number + status icon (left rail) */}
      <div className="flex shrink-0 flex-col items-center gap-1 pt-0.5">
        <span className="text-xs font-mono text-secondary tabular-nums">
          {String(row.stageNumber).padStart(2, '0')}
        </span>
        <Icon className={cn('h-4 w-4', STATUS_COLOR[row.status])} strokeWidth={1.5} />
      </div>

      {/* Body */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'text-sm font-medium',
              row.status === 'completed' ? 'text-primary' : 'text-secondary',
            )}
          >
            {row.label}
          </span>
          {row.outcome ? <OutcomeChip outcome={row.outcome} /> : null}
          {row.isClusterAnchor && row.status === 'current' ? (
            <span className="inline-flex items-center rounded-sm border border-accent/30 bg-accent/10 px-1.5 py-0.5 text-xs font-medium text-accent-strong">
              Up next
            </span>
          ) : null}
        </div>

        {/* Inline detail line: date + resolved date (if any) */}
        {row.date ? (
          <div className="mt-0.5 flex items-center gap-2 text-xs text-tertiary">
            <span>{row.date}</span>
            {row.resolvedDate ? (
              <>
                <span aria-hidden>·</span>
                <span>Resolved {row.resolvedDate}</span>
              </>
            ) : row.outcome && row.outcome !== 'ACCEPTED' ? (
              <>
                <span aria-hidden>·</span>
                <span className="text-warning-strong">Awaiting resolved date</span>
              </>
            ) : null}
          </div>
        ) : null}

        {/* Lock reason (secondary text) */}
        {row.lockReason ? (
          <div className="mt-1 text-xs text-tertiary">{row.lockReason}</div>
        ) : null}

        {/* Editable inputs */}
        {row.editable && !row.isInspectionEvent ? (
          <form onSubmit={handleSubmit} className="mt-2 flex items-center gap-2">
            <DmyDateInput
              maxDate={todayIso()}
              value={draftDate}
              onChange={setDraftDate}
              showValidationMessage={false}
              className="h-7 px-2 py-1 text-xs"
            />
            <button
              type="submit"
              disabled={!draftDate || submitting}
              className="rounded-sm bg-accent px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-strong disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Record'}
            </button>
          </form>
        ) : null}

        {row.editable && row.isInspectionEvent && onOpenInspection ? (
          <div className="mt-2">
            <button
              type="button"
              onClick={() =>
                onOpenInspection(row.stageNumber as 9 | 11 | 13)
              }
              className="rounded-sm border border-border-default bg-surface px-2.5 py-1 text-xs font-medium text-primary hover:border-accent hover:text-accent-strong"
            >
              {row.date ? 'Edit / Backfill' : 'Record inspection'}
            </button>
          </div>
        ) : null}

        {error ? <div className="mt-1 text-xs text-danger-strong">{error}</div> : null}
      </div>
    </div>
  );
}
